import {naturalSortByKey} from '@benchling/js-utils';
import React from 'react';

import isArchived from 'archive/isArchived';
import DataConnector from 'connect/DataConnector';
import {ManagedModelType} from 'models/ManagedModelDefinitions';
import {ALL_KEY} from 'models/managers/AllModelManager';
import {
  RequestSchema,
  SCHEMA_ID_REQUEST_SCHEMA_ALL,
  SCHEMA_OPTION_REQUEST_SCHEMA_ALL,
} from 'requests/RequestConstants';
import {RequestTaskSchema, SCHEMA_ID_REQUEST_TASK_SCHEMA_ALL} from 'requests/tasks/RequestTaskConstants';
import {SingleValueFilter} from 'search/SearchConstants';
import pickDefined from 'util/pickDefined';

import DropdownFilterForm, {prefixedToggleText} from './base/DropdownFilterForm';
import {BASE_FILTER_FORM_PROP_NAMES, BaseFilterFormProps, FilterStyle} from './base/FilterFormConstants';
import useFilterArchivedDropdownOptions, {
  ArchivableOptionData,
} from './base/useFilterArchivedDropdownOptions';

export function getRequestTaskSchemasForRequestSchema(
  allTaskSchemas: Array<RequestTaskSchema>,
  selectedAssaySchema: RequestSchema | typeof SCHEMA_OPTION_REQUEST_SCHEMA_ALL | null
): Array<RequestTaskSchema> {
  if (!selectedAssaySchema || selectedAssaySchema.id === SCHEMA_ID_REQUEST_SCHEMA_ALL) {
    return allTaskSchemas;
  }
  return allTaskSchemas.filter((taskSchema) =>
    // TODO(TypeScript): RequestSchema type should match any object where id != SCHEMA_ID_REQUEST_SCHEMA_ALL.
    (selectedAssaySchema as RequestSchema).requestTaskSchemaIds.includes(taskSchema.id)
  );
}

interface RequestTaskSchemaFilterFormProps extends BaseFilterFormProps {
  filter: SingleValueFilter;
  filterStyle: FilterStyle;
  onChangeFilter: (filter: SingleValueFilter) => void;
  onRemove?: () => void;
}

interface EnhanceProps {
  allTaskSchemas: Array<RequestTaskSchema>;
}

export interface RequestTaskSchemaFilterFormExtraProps {
  selectedAssaySchema: RequestSchema | typeof SCHEMA_OPTION_REQUEST_SCHEMA_ALL | null;
}

class AllRequestTaskSchemasDataConnector extends DataConnector<
  RequestTaskSchemaFilterFormProps & RequestTaskSchemaFilterFormExtraProps,
  EnhanceProps
> {
  enhanceProps(): EnhanceProps {
    return {
      allTaskSchemas: this.getOrFetchRequired(ManagedModelType.ALL_REQUEST_TASK_SCHEMAS, ALL_KEY),
    };
  }

  renderLoading(): JSX.Element {
    return (
      <RequestTaskSchemaFilterForm {...this.props} allTaskSchemas={[]} isLoading={true} isDisabled={true} />
    );
  }
}

function _buildOptionDatas(
  allTaskSchemas: Array<RequestTaskSchema>,
  validTaskSchemas: Array<RequestTaskSchema>
): Array<ArchivableOptionData> {
  return [
    ...[{name: 'Any', value: SCHEMA_ID_REQUEST_TASK_SCHEMA_ALL, isArchived: false}],
    ...naturalSortByKey(
      allTaskSchemas.map((requestTaskSchema) => ({
        name: requestTaskSchema.name,
        value: requestTaskSchema.id,
        hidden: !validTaskSchemas.includes(requestTaskSchema),
        isArchived: isArchived(requestTaskSchema),
      })),
      'name'
    ),
  ];
}

export function RequestTaskSchemaFilterForm(
  props: RequestTaskSchemaFilterFormProps & RequestTaskSchemaFilterFormExtraProps & EnhanceProps
): JSX.Element {
  const {allTaskSchemas, selectedAssaySchema, filter, filterStyle, onChangeFilter, onRemove} = props;
  const optionDatas = _buildOptionDatas(
    allTaskSchemas,
    getRequestTaskSchemasForRequestSchema(allTaskSchemas, selectedAssaySchema)
  );
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const onChangeValueBase = (value: string) => {
    onChangeFilter({...filter, value});
  };
  const filterArchivedDropdownOptionProps = useFilterArchivedDropdownOptions(
    optionDatas,
    'Show archived tasks',
    onChangeValueBase
  );

  const skeletonProps = pickDefined(props, ...BASE_FILTER_FORM_PROP_NAMES);
  return (
    <DropdownFilterForm
      {...skeletonProps}
      {...filterArchivedDropdownOptionProps}
      dropdownToggleText={prefixedToggleText('Task', optionDatas, filter)}
      filterStyle={filterStyle}
      isMulti={false}
      isSearchable={true}
      onRemove={onRemove}
      value={filter.value}
    />
  );
}

export default AllRequestTaskSchemasDataConnector.connect(RequestTaskSchemaFilterForm);
